/* ETOLIE — product films. Visible previews play silently; clicking opens the full film. */
(() => {
  if (customElements.get('etolie-films')) return;

  const icon = (name) => {
    const paths = {
      close: '<path d="m5 5 14 14M19 5 5 19"/>',
      left: '<path d="m14 5-7 7 7 7"/>',
      right: '<path d="m9 5 7 7-7 7"/>',
      arrow: '<path d="M3 12h17m-6-6 6 6-6 6"/>',
      play: '<path d="m7 4 14 8-14 8Z"/>',
      pause: '<path d="M8 5v14M16 5v14"/>',
      muted: '<path d="M10 4 5 8H2v8h3l5 4V4Zm5 5 6 6m0-6-6 6"/>',
      sound: '<path d="M10 4 5 8H2v8h3l5 4V4Zm5 4a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/>',
    };
    return `<svg viewBox="0 0 24 24" aria-hidden="true">${paths[name] || paths.play}</svg>`;
  };
  const secondsLabel = (milliseconds) => `${Math.max(1, Math.round(Number(milliseconds) / 1000) || 0)} s`;
  const filmCount = (count) => `${count} ${window.etolieT(count === 1 ? 'film' : count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? 'filmy' : 'filmów')}`;
  const safeURL = (value, sameOrigin = false) => {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol) || (sameOrigin && url.origin !== location.origin)) return '';
      return url.href;
    } catch { return ''; }
  };

  class EtolieFilms extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.list = this.querySelector('[data-film-list]');
      if (!this.list) return;
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.index = 0;
      this.playSequence = 0;
      this.seenPages = new Set();
      this.loadedLibraryPages = [];
      this.status = document.createElement('p');
      this.status.className = 'etolie-films__status visually-hidden';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.append(this.status);
      this.createDialog(options);
      this.connectPreviews(options);
      this.addEventListener('click', this.onClick.bind(this), options);
      this.list.addEventListener('scroll', () => this.updateArrows(), { ...options, passive: true });
      // Rotating a phone can cross the home-card breakpoint; keep its film playing.
      this.mobile.addEventListener('change', () => this.updateArrows(), options);
      document.addEventListener('visibilitychange', () => {
        this.syncPreviews();
        if (document.hidden) {
          this.video?.pause();
          this.rememberLibrary();
        }
      }, options);
      window.addEventListener('pagehide', () => {
        this.previewsSuspended = true;
        this.syncPreviews();
        this.closeViewer(false);
        this.rememberLibrary();
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.list);
      const count = this.querySelector('[data-film-count]');
      if (count) count.textContent = filmCount(this.cards.length);
      this.updateArrows();
      const restoring = this.connectLibraryMemory(options);
      if (this.hasAttribute('data-library') && !restoring) {
        // ETOLIE 06.09.2026 (P3, kontrola Claude): strona katalogu ma 48 produktów, ale filmów bywa w niej 3–15,
        // więc biblioteka dopełnia siatkę kolejnymi stronami, aż pokaże co najmniej data-films-min (24) filmów.
        this.fillLibrary(this.libraryMinimum(), true);
      }
    }

    disconnectedCallback() {
      this.previewsSuspended = true;
      this.previewObserver?.disconnect();
      this.syncPreviews();
      this.closeViewer(false);
      this.loadController?.abort();
      this.restoreController?.abort();
      this.resizeObserver?.disconnect();
      this.events?.abort();
      this.dialog?.remove();
      this.status?.remove();
      this.events = null;
      this.dialog = null;
      this.video = null;
    }

    get cards() { return Array.from(this.list?.querySelectorAll(':scope > [data-film-card]') || []); }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      if (this.restoringLibrary && event.target.closest('[data-film-open], .etolie-films__product, [data-films-more]')) {
        event.preventDefault();
        return;
      }
      if (event.target.closest('[data-films-restore]')) {
        this.restoreLibrary(this.libraryRestorePending);
        return;
      }
      const opener = event.target.closest('[data-film-open]');
      if (opener && this.contains(opener)) {
        this.libraryRestorePending = null;
        this.rememberLibrary(opener.closest('[data-film-card]'));
        this.openViewer(this.cards.indexOf(opener.closest('[data-film-card]')), opener);
        return;
      }
      const scroll = event.target.closest('[data-film-scroll]');
      if (scroll && this.contains(scroll)) {
        const first = this.cards[0];
        const step = first ? first.getBoundingClientRect().width + 10 : 170;
        this.list.scrollBy({ left: Number(scroll.dataset.filmScroll) * step, behavior: this.reducedMotion ? 'auto' : 'smooth' });
        return;
      }
      const more = event.target.closest('a[data-films-more]');
      if (more && this.contains(more) && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        const url = safeURL(more.getAttribute('href'), true);
        if (!url) return;
        event.preventDefault();
        // ETOLIE 06.09.2026 (P3): jedno dotknięcie = kolejne 24 filmy (albo wszystkie, gdy zostało mniej).
        this.fillLibrary(this.cards.length + this.libraryMinimum());
        return;
      }
      if (event.target.closest('.etolie-films__product, [data-film-product-link]')) {
        // Restore the document before the listing remembers the position for Back.
        this.closeViewer(false);
        this.closest('etolie-listing-stream')?.rememberScroll?.();
        this.libraryRestorePending = null;
        this.rememberLibrary(event.target.closest('[data-film-card]'));
      }
    }

    get reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

    connectPreviews(options) {
      this.previews = new Map();
      this.previewsSuspended = false;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motionPreference.addEventListener('change', () => this.syncPreviews(), options);
      window.addEventListener('pageshow', () => {
        this.previewsSuspended = false;
        this.syncPreviews();
      }, options);
      this.previewObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const preview = this.previews.get(entry.target);
          if (preview) preview.visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
        }
        this.syncPreviews();
      }, { threshold: [0, 0.1] });
      this.observePreviews();
    }

    observePreviews() {
      if (!this.previewObserver) return;
      for (const card of this.cards) {
        const cover = card.querySelector('[data-film-open]');
        if (!cover || this.previews.has(cover)) continue;
        this.previews.set(cover, { visible: false, video: null });
        this.previewObserver.observe(cover);
      }
    }

    syncPreviews() {
      if (!this.previews) return;
      const allowed = this.isConnected && !this.previewsSuspended && !document.hidden
        && !this.dialog?.open && !this.motionPreference.matches;
      for (const [cover, preview] of this.previews) {
        if (!allowed || !preview.visible || !cover.isConnected) {
          if (preview.video) {
            preview.video.pause();
            preview.video.removeAttribute('src');
            preview.video.load();
            preview.video.remove();
            preview.video = null;
          }
          continue;
        }
        if (preview.video) continue;
        const source = safeURL(cover.closest('[data-film-card]')?.dataset.filmSrc);
        if (!source) continue;
        const video = document.createElement('video');
        video.className = 'etolie-films__preview';
        video.setAttribute('muted', '');
        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        video.autoplay = true;
        video.preload = 'none';
        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        video.poster = cover.querySelector('img')?.currentSrc || '';
        // Wait for resource selection, which can abort an immediate play() on iOS.
        const start = () => {
          if (preview.video === video && video.paused) video.play().catch(() => {});
        };
        video.addEventListener('loadstart', start, { once: true });
        video.src = source;
        preview.video = video;
        cover.append(video);
        setTimeout(start, 0);
        // A blocked autoplay leaves the poster and the normal full-player button usable.
      }
    }

    updateArrows() {
      if (!this.list) return;
      const left = this.querySelector('[data-film-scroll="-1"]');
      const right = this.querySelector('[data-film-scroll="1"]');
      if (left) left.disabled = this.list.scrollLeft <= 2;
      if (right) right.disabled = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 2;
    }

    createDialog(options) {
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'etolie-films__dialog';
      this.dialog.setAttribute('aria-label', window.etolieT("Film produktu"));
      this.dialog.innerHTML = `
        <div class="etolie-films__viewer">
          <header class="etolie-films__viewer-top">
            <button type="button" data-film-close aria-label="${window.etolieT("Zamknij film")}">${icon('close')}</button>
            <span data-film-position></span>
            <button type="button" data-film-mute aria-label="${window.etolieT("Włącz dźwięk")}">${icon('muted')}</button>
          </header>
          <div class="etolie-films__stage">
            <video playsinline muted loop preload="none"></video>
            <button type="button" class="etolie-films__play-main" data-film-play aria-label="${window.etolieT("Odtwórz film")}"><span>${icon('play')}</span></button>
            <p class="etolie-films__error" data-film-error role="status" hidden>${window.etolieT("Nie udało się uruchomić filmu. Dotknij przycisku odtwarzania, aby spróbować ponownie.")}</p>
            <div class="etolie-films__controls">
              <button type="button" class="etolie-films__round-control" data-film-toggle aria-label="${window.etolieT("Odtwórz film")}">${icon('play')}</button>
              <span class="etolie-films__time" data-film-time></span>
              <div class="etolie-films__next-controls">
                <button type="button" class="etolie-films__round-control" data-film-previous aria-label="${window.etolieT("Poprzedni film")}">${icon('left')}</button>
                <button type="button" class="etolie-films__round-control" data-film-next aria-label="${window.etolieT("Następny film")}">${icon('right')}</button>
              </div>
            </div>
            <div class="etolie-films__progress" aria-hidden="true"><i></i></div>
          </div>
          <footer class="etolie-films__viewer-product">
            <div class="etolie-films__brand-price"><span class="etolie-films__vendor" data-film-view-vendor></span><span data-film-view-price></span></div>
            <h2 data-film-view-title></h2>
            <p class="etolie-films__color" data-film-view-color></p>
            <div class="etolie-films__viewer-actions">
              <a class="etolie-films__cta" data-film-product-link><span>${window.etolieT("Zobacz produkt")}</span>${icon('arrow')}</a>
              <a class="etolie-quick-add-trigger" data-film-quick-add data-etolie-quick-add="" data-quick-color-known="false" aria-haspopup="dialog"><span class="etolie-quick-add-trigger__icon" aria-hidden="true"></span></a>
            </div>
          </footer>
        </div>`;
      this.append(this.dialog);
      this.video = this.dialog.querySelector('video');
      this.video.muted = true;
      this.dialog.querySelector('[data-film-close]').addEventListener('click', () => this.closeViewer(), options);
      this.dialog.querySelector('[data-film-play]').addEventListener('click', (event) => {
        // A touch swipe on the paused poster can also produce a synthetic click.
        if (event.detail !== 0 && performance.now() < (this.suppressPlayClickUntil || 0)) {
          event.preventDefault();
          return;
        }
        this.play();
      }, options);
      this.dialog.querySelector('[data-film-toggle]').addEventListener('click', () => this.video.paused ? this.play() : this.video.pause(), options);
      this.dialog.querySelector('[data-film-mute]').addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        this.syncMute();
      }, options);
      this.dialog.querySelector('[data-film-previous]').addEventListener('click', () => this.change(-1), options);
      this.dialog.querySelector('[data-film-next]').addEventListener('click', () => this.change(1), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeViewer(); }, options);
      this.dialog.addEventListener('close', () => this.finishClose(), options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.change(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.video.addEventListener('play', () => this.syncPlayback(), options);
      this.video.addEventListener('pause', () => this.syncPlayback(), options);
      this.video.addEventListener('error', () => { if (this.dialog.open && this.video.getAttribute('src')) this.showError(); }, options);
      this.video.addEventListener('timeupdate', () => {
        const progress = this.video.duration ? this.video.currentTime / this.video.duration : 0;
        this.dialog.querySelector('.etolie-films__progress i').style.transform = `scaleX(${Math.min(1, Math.max(0, progress))})`;
      }, options);
      const stage = this.dialog.querySelector('.etolie-films__stage');
      stage.addEventListener('touchstart', (event) => {
        this.suppressPlayClickUntil = 0;
        const control = event.target.closest('button, a');
        if (event.touches.length !== 1 || (control && !control.matches('[data-film-play]'))) { this.touch = null; return; }
        this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { ...options, passive: true });
      stage.addEventListener('touchcancel', () => { this.touch = null; }, options);
      stage.addEventListener('touchend', (event) => {
        if (!this.touch || !event.changedTouches[0]) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          this.suppressPlayClickUntil = performance.now() + 700;
          this.change(dx < 0 ? 1 : -1);
        }
      }, { ...options, passive: true });
    }

    openViewer(index, opener) {
      if (index < 0 || (!this.mobile.matches && !this.hasAttribute('data-library'))) return;
      if (typeof this.dialog.showModal !== 'function') {
        const url = safeURL(this.cards[index]?.dataset.filmUrl, true);
        if (url) location.assign(url);
        return;
      }
      this.opener = opener;
      this.savedScroll = { x: window.scrollX, y: window.scrollY };
      this.restoreFocus = true;
      this.closest('etolie-listing-stream')?.rememberScroll?.();
      this.video.muted = true;
      this.index = index;
      this.renderFilm();
      this.setAttribute('data-film-viewer-open', '');
      document.documentElement.classList.add('etolie-films-open');
      this.dialog.showModal();
      this.syncPreviews();
      this.dialog.querySelector('[data-film-close]').focus({ preventScroll: true });
      this.play();
    }

    clearMedia() {
      this.playSequence += 1;
      if (!this.video) return;
      this.video.pause();
      if (this.video.hasAttribute('src')) {
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    renderFilm() {
      this.clearMedia();
      const card = this.cards[this.index];
      if (!card) return;
      const data = card.dataset;
      this.current = data;
      const poster = safeURL(data.filmPoster);
      if (poster) this.video.poster = poster;
      else this.video.removeAttribute('poster');
      this.video.setAttribute('aria-label', `Film: ${data.filmTitle || 'Produkt'}`);
      this.dialog.setAttribute('aria-label', `Film: ${data.filmTitle || 'Produkt'}`);
      const texts = {
        '[data-film-position]': `Filmy · ${String(this.index + 1).padStart(2, '0')} / ${String(this.cards.length).padStart(2, '0')}`,
        '[data-film-view-vendor]': data.filmVendor,
        '[data-film-view-price]': data.filmPrice,
        '[data-film-view-title]': data.filmTitle,
        '[data-film-view-color]': data.filmColor,
        '[data-film-time]': `${secondsLabel(data.filmDuration)}${data.filmVendor ? ` · ${data.filmVendor}` : ''}`,
      };
      Object.entries(texts).forEach(([selector, value]) => { this.dialog.querySelector(selector).textContent = value || ''; });
      this.dialog.querySelector('[data-film-view-color]').hidden = !data.filmColor;
      const link = this.dialog.querySelector('[data-film-product-link]');
      const productURL = safeURL(data.filmUrl, true);
      if (productURL) link.href = productURL;
      else link.removeAttribute('href');
      const quickAdd = this.dialog.querySelector('[data-film-quick-add]');
      if (productURL) quickAdd.href = productURL;
      else quickAdd.removeAttribute('href');
      quickAdd.dataset.etolieQuickAdd = productURL;
      quickAdd.dataset.quickColorKnown = data.filmColorKnown === 'true' ? 'true' : 'false';
      quickAdd.setAttribute('aria-label', `${window.etolieT('Dodaj do koszyka')}: ${data.filmTitle || ''}`);
      quickAdd.hidden = !productURL || !card.querySelector('[data-etolie-quick-add]');
      this.dialog.querySelector('[data-film-error]').hidden = true;
      this.dialog.querySelector('.etolie-films__progress i').style.transform = 'scaleX(0)';
      this.dialog.querySelector('[data-film-previous]').disabled = this.cards.length < 2;
      this.dialog.querySelector('[data-film-next]').disabled = this.cards.length < 2;
      this.syncMute();
      this.syncPlayback();
    }

    async play() {
      if (!this.dialog?.open || !this.current) return;
      const source = safeURL(this.current.filmSrc);
      if (!source) { this.showError(); return; }
      this.dialog.querySelector('[data-film-error]').hidden = true;
      if (this.video.error) this.clearMedia();
      const sequence = ++this.playSequence;
      if (!this.video.getAttribute('src')) this.video.src = source;
      try { await this.video.play(); }
      catch { if (sequence === this.playSequence && this.dialog?.open) this.showError(); }
    }

    showError() {
      this.dialog.querySelector('[data-film-error]').hidden = false;
      this.syncPlayback();
    }

    syncPlayback() {
      if (!this.dialog || !this.video) return;
      const playing = !this.video.paused;
      const toggle = this.dialog.querySelector('[data-film-toggle]');
      toggle.innerHTML = icon(playing ? 'pause' : 'play');
      toggle.setAttribute('aria-label', playing ? window.etolieT("Zatrzymaj film") : window.etolieT("Odtwórz film"));
      this.dialog.querySelector('[data-film-play]').hidden = playing;
    }

    syncMute() {
      const mute = this.dialog.querySelector('[data-film-mute]');
      mute.innerHTML = icon(this.video.muted ? 'muted' : 'sound');
      mute.setAttribute('aria-label', this.video.muted ? window.etolieT("Włącz dźwięk") : window.etolieT("Wyłącz dźwięk"));
      mute.setAttribute('aria-pressed', String(!this.video.muted));
    }

    change(delta) {
      if (!this.dialog?.open || this.cards.length < 2) return;
      this.index = (this.index + delta + this.cards.length) % this.cards.length;
      this.renderFilm();
      this.play();
    }

    closeViewer(restoreFocus = true) {
      if (!this.dialog) return;
      this.restoreFocus = restoreFocus;
      this.clearMedia();
      if (this.dialog.open) this.dialog.close();
      this.finishClose();
    }

    finishClose() {
      this.removeAttribute('data-film-viewer-open');
      if (!this.savedScroll) return;
      this.clearMedia();
      document.documentElement.classList.remove('etolie-films-open');
      const { x, y } = this.savedScroll;
      this.savedScroll = null;
      if (this.restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
      this.opener = null;
      this.touch = null;
      this.syncPreviews();
    }

    cardKey(card) {
      if (this.dataset.dedupe === 'media') return card.dataset.filmId || card.dataset.filmSrc;
      if (card.dataset.filmProductId) return card.dataset.filmProductId;
      try { return new URL(card.dataset.filmUrl, location.origin).pathname; }
      catch { return card.dataset.filmId; }
    }

    libraryMemoryKey() {
      const url = new URL(location.href);
      const parameters = new URLSearchParams();
      url.searchParams.forEach((value, name) => {
        if (['view', 'page', 'sort_by'].includes(name) || name.startsWith('filter.')) parameters.append(name, value);
      });
      parameters.sort();
      return `etolie:films:v1:${url.pathname}?${parameters}`;
    }

    connectLibraryMemory(options) {
      if (!this.hasAttribute('data-library')) return false;
      try { history.scrollRestoration = 'manual'; } catch { /* Browser policy. */ }
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.restoreLibrary(this.readLibraryMemory());
      }, options);
      const isBack = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
      const state = isBack && this.readLibraryMemory();
      if (!state) return false;
      this.restoreLibrary(state);
      return true;
    }

    readLibraryMemory() {
      try {
        const state = JSON.parse(sessionStorage.getItem(this.libraryMemoryKey()) || 'null');
        if (!state || state.version !== 1 || state.dedupe !== (this.dataset.dedupe || 'product') || !Array.isArray(state.pages)) return null;
        if (!Number.isFinite(state.savedAt) || Date.now() - state.savedAt > 86400000 || !Number.isFinite(state.offset)) return null;
        if (!state.anchor || (typeof state.anchor.media !== 'string' && typeof state.anchor.product !== 'string')) return null;
        state.pages = [...new Set(state.pages.filter((url) => this.libraryPageURL(url)))].slice(0, 20);
        return state;
      } catch { return null; }
    }

    rememberLibrary(preferredCard) {
      if (!this.hasAttribute('data-library') || this.restoringLibrary || this.libraryRestorePending || !this.isConnected) return;
      const cards = this.cards;
      if (!cards.length) return;
      const y = this.savedScroll?.y ?? window.scrollY;
      let anchor = preferredCard?.isConnected ? preferredCard : null;
      if (!anchor) {
        // First column of the nearest row remains a stable anchor at either grid width.
        anchor = cards.find((card) => card.getBoundingClientRect().bottom > 70) || cards[cards.length - 1];
      }
      try {
        sessionStorage.setItem(this.libraryMemoryKey(), JSON.stringify({
          version: 1, savedAt: Date.now(), dedupe: this.dataset.dedupe || 'product',
          pages: this.loadedLibraryPages.slice(0, 20),
          anchor: { media: anchor.dataset.filmId || '', product: anchor.dataset.filmProductId || '' },
          offset: anchor.getBoundingClientRect().top, scrollY: y,
        }));
      } catch { /* Browsing still works without storage. */ }
    }

    libraryPageURL(value, forFetch = true) {
      const safe = safeURL(value, true);
      if (!safe) return '';
      const url = new URL(safe);
      const current = new URL(location.href);
      if (url.pathname !== current.pathname || url.searchParams.get('view') !== current.searchParams.get('view')) return '';
      const page = Number(url.searchParams.get('page') || 1);
      if (!Number.isInteger(page) || page < 1) return '';
      if (!forFetch) url.searchParams.delete('section_id');
      else if (this.dataset.filmsSectionId) url.searchParams.set('section_id', this.dataset.filmsSectionId);
      return url.href;
    }

    async fetchLibraryPage(value, signal) {
      const url = this.libraryPageURL(value);
      if (!url) throw new Error('Nieprawidłowy adres strony filmów');
      const response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok || !this.libraryPageURL(response.url)) throw new Error('Nieprawidłowa odpowiedź');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = parsed.querySelector('etolie-films[data-library]');
      if (!source || !source.querySelector('[data-film-list]')) throw new Error('Brak listy filmów');
      const sourceNext = source.querySelector('a[data-films-more]') || parsed.querySelector('a[data-films-more]');
      // href remains a complete page for Cmd/Ctrl-click and JavaScript-free navigation.
      // Only the fetch URL receives section_id.
      const nextURL = sourceNext && this.libraryPageURL(sourceNext.getAttribute('href'), false);
      const nextLink = nextURL ? document.importNode(sourceNext, true) : null;
      if (nextLink) nextLink.href = nextURL;
      return { url, source, nextLink };
    }

    appendLibraryPage(page, keys) {
      const fragment = document.createDocumentFragment();
      let added = 0;
      let firstAdded = null;
      page.source.querySelectorAll('[data-film-list] > [data-film-card]').forEach((card) => {
        const key = this.cardKey(card);
        if (!key || keys.has(key)) return;
        keys.add(key);
        const clone = document.importNode(card, true);
        firstAdded ||= clone;
        fragment.append(clone);
        added += 1;
      });
      this.list.append(fragment);
      this.observePreviews();
      this.seenPages.add(page.url);
      if (!this.loadedLibraryPages.includes(page.url)) this.loadedLibraryPages.push(page.url);
      return { added, firstAdded };
    }

    libraryAnchor(state) {
      return this.cards.find((card) => state.anchor.media && card.dataset.filmId === state.anchor.media)
        || this.cards.find((card) => state.anchor.product && card.dataset.filmProductId === state.anchor.product);
    }

    async restoreLibrary(state) {
      if (!state || this.restoringLibrary || !this.hasAttribute('data-library')) return;
      this.restoringLibrary = true;
      this.libraryRestorePending = state;
      this.querySelector('[data-films-restore]')?.remove();
      this.restoreController = new AbortController();
      const controller = this.restoreController;
      this.list.inert = true;
      this.list.setAttribute('aria-busy', 'true');
      this.status.textContent = window.etolieT("Przywracanie oglądanych filmów…");
      const keys = new Set(this.cards.map((card) => this.cardKey(card)));
      try {
        for (const recordedURL of state.pages.slice(0, 20)) {
          if (this.libraryAnchor(state)) break;
          const url = this.libraryPageURL(recordedURL);
          if (!url || this.seenPages.has(url)) continue;
          const page = await this.fetchLibraryPage(url, controller.signal);
          if (controller.signal.aborted || !this.isConnected) return;
          this.appendLibraryPage(page, keys);
          const oldLink = this.querySelector('a[data-films-more]');
          if (page.nextLink) {
            if (oldLink) oldLink.replaceWith(page.nextLink);
            else this.querySelector('.etolie-films__bottom')?.append(page.nextLink);
          } else oldLink?.remove();
        }
        // Intrinsic poster dimensions reserve the grid; wait one layout frame before measuring.
        // ETOLIE 06.09.2026 (kontrola Claude): gdy karta nie rysuje klatek (tło, przeglądarka bez animacji po „wstecz”),
        // requestAnimationFrame nigdy nie wraca i przywracanie wisiało z listą `inert`; limit 150 ms domyka je zawsze.
        await new Promise((resolve) => {
          const timer = setTimeout(resolve, 150);
          requestAnimationFrame(() => { clearTimeout(timer); resolve(); });
        });
        if (controller.signal.aborted || !this.isConnected) return;
        const anchor = this.libraryAnchor(state);
        const y = anchor ? window.scrollY + anchor.getBoundingClientRect().top - state.offset : Number(state.scrollY) || 0;
        window.scrollTo({ top: Math.max(0, y), behavior: 'instant' });
        this.libraryRestorePending = null;
        this.status.textContent = anchor ? window.etolieT("Przywrócono oglądane miejsce.") : window.etolieT("Lista filmów została zaktualizowana.");
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = window.etolieT("Nie udało się przywrócić wszystkich filmów. Możesz spróbować ponownie.");
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'etolie-films__back';
        retry.setAttribute('data-films-restore', '');
        retry.textContent = window.etolieT("Przywróć oglądane miejsce");
        this.querySelector('.etolie-films__heading')?.append(retry);
      } finally {
        this.restoringLibrary = false;
        this.list.inert = false;
        this.list.removeAttribute('aria-busy');
      }
    }

    libraryMinimum() {
      const minimum = Number(this.dataset.filmsMin || 24);
      return Number.isInteger(minimum) && minimum > 0 ? minimum : 24;
    }

    /**
     * ETOLIE 06.09.2026 (P3, kontrola Claude): dociąga kolejne strony katalogu, aż w siatce będzie `target` filmów
     * albo skończą się strony. `quiet` (start strony) nie przenosi fokusu ani nie ogłasza dodanych filmów.
     */
    async fillLibrary(target, quiet = false) {
      if (this.filling || this.restoringLibrary) return;
      this.filling = true;
      this.setAttribute('data-films-filling', '');
      try {
        for (let round = 0; round < 12 && this.cards.length < target; round += 1) {
          const link = this.querySelector('a[data-films-more]');
          const url = link && safeURL(link.getAttribute('href'), true);
          if (!url) {
            if (this.cards.length === 0) this.status.textContent = window.etolieT("Obecnie nie ma dostępnych filmów.");
            break;
          }
          const before = this.cards.length;
          await this.loadMore(link, url, { quiet: true });
          if (!this.isConnected || this.cards.length === before) break;
        }
      } finally {
        this.filling = false;
        this.removeAttribute('data-films-filling');
      }
      if (!quiet && this.isConnected) {
        const count = this.querySelector('[data-film-count]');
        if (count) count.textContent = filmCount(this.cards.length);
        const link = this.querySelector('a[data-films-more]');
        this.status.textContent = link ? window.etolieT('Pokazujemy {count}.', {count: filmCount(this.cards.length)}) : window.etolieT("To wszystkie dostępne filmy.");
      }
    }

    async loadMore(link, initialURL, { quiet = false } = {}) {
      if (this.loading || this.restoringLibrary) return;
      this.loading = true;
      this.loadController = new AbortController();
      const controller = this.loadController;
      const originalLabel = link.textContent;
      link.textContent = 'Wczytywanie…';
      link.setAttribute('aria-disabled', 'true');
      this.list.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      let currentURL = initialURL;
      let nextLink = null;
      let added = 0;
      let firstAdded = null;
      const keys = new Set(this.cards.map((card) => this.cardKey(card)));
      try {
        for (let scan = 0; currentURL && scan < 20 && added === 0; scan += 1) {
          if (this.seenPages.has(this.libraryPageURL(currentURL))) throw new Error('Powtórzona strona filmów');
          const page = await this.fetchLibraryPage(currentURL, controller.signal);
          if (controller.signal.aborted || !this.isConnected) return;
          const result = this.appendLibraryPage(page, keys);
          added += result.added;
          firstAdded ||= result.firstAdded;
          nextLink = page.nextLink;
          currentURL = nextLink?.href || '';
        }
        if (nextLink) link.replaceWith(nextLink);
        else link.remove();
        const count = this.querySelector('[data-film-count]');
        if (count) count.textContent = filmCount(this.cards.length);
        if (!quiet) {
          this.status.textContent = added ? window.etolieT('Dodano {count}.', {count: filmCount(added)}) : currentURL ? window.etolieT("Przejrzeliśmy kolejne produkty. Możesz wczytać następne filmy.") : window.etolieT("To wszystkie dostępne filmy.");
          const focus = firstAdded?.querySelector('[data-film-open], a[href]') || nextLink;
          if (focus) focus.focus({ preventScroll: true });
          else this.status.setAttribute('tabindex', '-1');
          if (!focus) this.status.focus({ preventScroll: true });
        }
        this.updateArrows();
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Already scanned empty pages need not be fetched again after a network failure.
        const retryURL = this.libraryPageURL(currentURL, false);
        if (retryURL) link.href = retryURL;
        link.textContent = window.etolieT("Spróbuj ponownie");
        this.status.textContent = window.etolieT("Nie udało się wczytać kolejnych filmów. Spróbuj ponownie.");
      } finally {
        this.loading = false;
        this.list?.removeAttribute('aria-busy');
        if (link.isConnected) {
          link.removeAttribute('aria-disabled');
          if (link.textContent === 'Wczytywanie…') link.textContent = originalLabel;
        }
      }
    }
  }
  customElements.define('etolie-films', EtolieFilms);
})();
